import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { extractApiErrorMessage } from '@chiroup/client-core/functions/extractApiErrorMessage';
import { Safari16API } from '@chiroup/client-core/functions/Safari16API';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { Patient } from '@chiroup/core/types/Patient.type';
import { HealthcomInsurance } from '@chiroup/core/types/PatientInsurance.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  SurveyDraftInput,
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';
import { kioskSession } from './kioskSession';
import { portalSession } from './portalSession';
import { requestSession } from './requestSession';

// Public PatientRequest service. The auth bootstrap (`/requests/auth`)
// is unauthenticated and calls the schedule backend's `api-noauth`
// API. Once the validationCode is verified the backend issues a
// request-scoped session token that we stash in `requestSession`;
// every subsequent call uses `api` (whose Bearer header comes from
// `main.tsx`, which falls back to `requestSession` when no patient
// session is present).
//
// `validationCode` parameters are kept on every method signature so
// existing callers don't have to change at the same time, but they
// are no longer sent over the wire — the schedule backend's wrapper
// trusts the Bearer session instead.

const API = 'api';
const API_NOAUTH = 'api-noauth';
const INVALID_API_NAME_MESSAGE = 'API name is invalid.';

const withInvalidApiNameFallback = async <T>(
  request: () => Promise<T>,
  fallback: () => Promise<T>
): Promise<T> => {
  try {
    return await request();
  } catch (err) {
    if (extractApiErrorMessage(err, '') !== INVALID_API_NAME_MESSAGE) {
      throw err;
    }

    return fallback();
  }
};

// Per-call Authorization header. Same defense as in `kiosk.service`:
// `main.tsx`'s fallback chain prefers `portalSession` when it's set,
// which would route a request-flow call to a patient-scoped token if
// the device also had a portal login. Per-call headers override the
// global callback (Amplify v6: `{...libraryConfigHeaders, ...invocationHeaders}`
// in api-rest's publicApis), so when we have a request/kiosk token
// we always send it.
//
// Important: when we DON'T have a request/kiosk token, return an
// empty object — not `{ Authorization: '' }` — so the per-call
// override leaves the global callback's header intact. The legacy
// empty-string form clobbered the global `Bearer <portal-token>`
// with `''`, making the Lambda see no token and return
// "Missing session token". `/requests/*` endpoints accept `patient`
// scope, so falling back to a portal session is the correct behavior
// when there's no narrower token available.
const requestAuthHeaders = (opts?: { kiosk?: boolean }): Record<string, string> => {
  const token = opts?.kiosk ? kioskSession.getToken() : requestSession.getToken();

  if (!token) return {};

  return { Authorization: `Bearer ${token}` };
};

export type RequestBootstrap = {
  steps: RequestStep[];
  language: 'en' | 'es';
  gender: Genders;
  age: number;
  id: string;
  ccsFollowups?: string[];
  guardian?: boolean;
  validationCode?: string | number;
  encounterId?: string;
  prMap?: { [key: string]: string };
  // Needed by BasicInformation (TextingConsentCheckboxes) and Insurance
  // (payor autocomplete). Populated from the `/requests/auth` response.
  clinicId?: number;
  smsConsent?: string | null;
  smsConsentMarketing?: string | null;
  // Patient's existing complaints, surfaced for reevaluation surveys so
  // the legacy <ReevalSurvey> can build per-complaint questions for each
  // active complaint before the new-problems prompt.
  complaints?: any[];
};

export type ClinicInfo = {
  logo?: string;
  name?: string;
  primaryColor?: string;
  accentColor?: string;
  doNotAllowUnrelatedRegions?: boolean;
};

export type AuthRequestResponse = {
  message?: string;
  data?: {
    phone?: string;
    email?: string;
    language?: 'en' | 'es';
    validationCode?: string | number;
    clinicInfo?: ClinicInfo;
    codeSent?: boolean;
    // Server returns the full request payload alongside validationCode
    // once the user has authed; we treat the rest of the body as the
    // bootstrap shape.
  } & Partial<RequestBootstrap>;
};

// Single retry after 1s on a transport-level failure — handles Safari's
// transient "Load failed" (a rejected fetch, not an HTTP error) that hits
// iOS when resuming from the camera or on a flaky mobile connection. HTTP
// error statuses are returned as-is for the caller to inspect via `resp.ok`.
const fetchWithRetry = async (url: string, init?: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (_err) {
    await new Promise(r => setTimeout(r, 1000));

    return await fetch(url, init);
  }
};

const s3PostWithRetry = (url: string, form: FormData): Promise<Response> =>
  fetchWithRetry(url, { method: 'POST', body: form });

const requestService = () => {
  const apiUrl = import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string;

  // Auth — GET asks the server for available delivery methods or sends a
  // code. Code verification uses POST so the validationCode never lands
  // in browser history, referrers, or API Gateway query logs.
  const authRequest = async (params: {
    requestId: string;
    validationCode?: string | number;
    method?: string;
  }): Promise<AuthRequestResponse> => {
    const queryParams: { [key: string]: string } = { requestId: params.requestId };

    if (params.validationCode != null && params.requestId) {
      const options = {
        body: {
          requestId: params.requestId,
          validationCode: String(params.validationCode),
        },
      };
      const res = await withInvalidApiNameFallback<AuthRequestResponse>(
        () => ChiroUpAPI.post(API_NOAUTH, '/requests/auth', options),
        () => Safari16API.post(API_NOAUTH, '/requests/auth', options)
      );
      const data = res?.data as any;

      if (data?.token) {
        requestSession.setToken(data.token, params.requestId, data.tokenExpiresAt);
      }

      return res;
    }

    if (params.method && params.requestId) {
      queryParams.method = params.method;
    }

    const options = { queryParams };
    const res = await withInvalidApiNameFallback<AuthRequestResponse>(
      () => ChiroUpAPI.get(API_NOAUTH, '/requests/auth', options),
      () => Safari16API.get(API_NOAUTH, '/requests/auth', options)
    );

    const data = res?.data as any;

    if (data?.token) {
      requestSession.setToken(data.token, params.requestId, data.tokenExpiresAt);
    }

    return res;
  };

  const saveSurveyDraft = ({
    requestId,
    patientSurveyId,
    draft,
    prMap,
    kiosk,
  }: {
    requestId: string;
    patientSurveyId: string;
    draft: SurveyDraftInput;
    prMap?: { [key: string]: string };
    kiosk?: boolean;
  }): Promise<{ saved: boolean; updatedAt: number }> => {
    const pr = prMap?.[patientSurveyId] || requestId;
    const options = {
      headers: requestAuthHeaders({ kiosk }),
      body: draft,
    };

    return withInvalidApiNameFallback(
      () => ChiroUpAPI.put(API, `/requests/${pr}/survey/${patientSurveyId}/draft`, options),
      () => Safari16API.put(API, `/requests/${pr}/survey/${patientSurveyId}/draft`, options)
    );
  };

  // Survey question answer submit — same endpoint the legacy app
  // already uses. Drives next-question navigation in the runner.
  const submitSurveyAnswer = async ({
    requestId,
    patientSurveyId,
    patientSurveyQuestionId,
    answer,
    validationCode,
    prMap,
    encounterId,
    kiosk,
    remainingSteps,
  }: {
    requestId: string;
    patientSurveyId: string;
    patientSurveyQuestionId: string;
    answer: SurveyQuestionAnswer;
    validationCode: string | number;
    prMap?: { [key: string]: string };
    encounterId?: string;
    kiosk?: boolean;
    remainingSteps?: number[];
  }): Promise<{ question?: SurveyQuestion; progress?: SurveyProgress }> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.put(
      API,
      `/requests/${pr}/survey/${patientSurveyId}/${patientSurveyQuestionId}`,
      {
        headers: requestAuthHeaders({ kiosk }),
        body: {
          patientSurveyQuestionAnswer: answer,
          validationCode,
          remainingSteps: remainingSteps ?? [],
          prMap: prMap ?? {},
          kiosk: !!kiosk,
          encounterId,
        },
      }
    );
  };

  // Previous-question lookup for the back button.
  const getPreviousQuestion = async ({
    requestId,
    patientSurveyId,
    questionId,
    prMap,
    kiosk,
  }: {
    requestId: string;
    patientSurveyId: string;
    questionId: string;
    prMap?: { [key: string]: string };
    kiosk?: boolean;
  }): Promise<{
    question: SurveyQuestion;
    previousQuestion: string;
    progress: SurveyProgress;
  }> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.get(API, `/requests/${pr}/survey/${patientSurveyId}/previous/${questionId}`, {
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  // Consent — validationCode-authed PUT that stitches the signed
  // acknowledgment page onto the original consent PDF. `preview: true`
  // returns a downloadable URL without marking the consent complete.
  const submitConsent = async ({
    requestId,
    patientConsentId,
    consentUrl,
    signature,
    validationCode,
    prMap,
    remainingSteps,
    preview,
    kiosk,
  }: {
    requestId: string;
    patientConsentId: string;
    consentUrl: string;
    signature: string | undefined;
    validationCode: string | number;
    prMap?: { [key: string]: string };
    remainingSteps?: number[];
    preview?: boolean;
    kiosk?: boolean;
  }): Promise<{ url?: string }> => {
    const pr = prMap?.[patientConsentId] || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/consent/${patientConsentId}`, {
      headers: requestAuthHeaders({ kiosk }),
      body: {
        consent: consentUrl,
        signature,
        preview: !!preview,
        validationCode,
        remainingSteps: remainingSteps ?? [],
      },
    });
  };

  // Photo ID / insurance card uploads — pre-sign + POST to S3 directly.
  const uploadFile = async ({
    type,
    requestId,
    validationCode,
    file,
    side,
    prMap,
    surveyType,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
    validationCode: string | number;
    file: File;
    side: 'front' | 'back';
    prMap?: { [key: string]: string };
    surveyType?: string;
  }): Promise<string> => {
    const fileType = file.type.split('/')[1];
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';

    // Pick the per-step request id:
    //   1) prefer the type-specific prMap entry (set by the public-
    //      request bootstrap),
    //   2) fall back to the umbrella `requestId` (also from the public
    //      flow, or `detail.id` in the portal `RequestFormRunner`),
    //   3) finally, for the kiosk flow — where `bootstrap.id` is `''`
    //      and the type-specific prMap key may not be present — fall
    //      through to any other prMap value. The kiosk's prMap entries
    //      all target the same patient's request bundle, so any non-
    //      empty entry resolves to a valid `/requests/{id}/...` URL.
    let pr =
      type === 'photoId'
        ? prMap?.uploadPhotoId || requestId
        : prMap?.uploadInsuranceCard || requestId;

    if (!pr && prMap) {
      pr = Object.values(prMap).find(v => !!v) || '';
    }

    if (!pr) {
      throw new Error('No request id available for upload.');
    }

    const surveyTypeQuery =
      type === 'insuranceCard' ? `&surveyType=${surveyType ?? 'insuranceCard'}` : '';

    // Use whichever session is active — direct request, portal, or
    // kiosk. Same fallback chain as Amplify's global headers callback
    // in `main.tsx`.
    const token = requestSession.getToken() || portalSession.getToken() || kioskSession.getToken();
    const resp = await fetchWithRetry(
      `${apiUrl}/requests/${pr}/${subPath}?fileType=${fileType}&side=${side}${surveyTypeQuery}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );

    if (!resp.ok) {
      throw new Error(`Failed to start upload (${resp.status})`);
    }

    const res: { fields: { [key: string]: string }; url: string } = await resp.json();
    const form = new FormData();

    Object.entries(res.fields).forEach(([key, val]) => form.append(key, val));
    form.append('file', file);

    const s3Resp = await s3PostWithRetry(res.url, form);

    if (!s3Resp.ok) {
      throw new Error(`Upload to S3 failed (${s3Resp.status})`);
    }

    return res.fields.key;
  };

  // Convenience wrapper for the inline insurance card upload — matches
  // the legacy `Insurance.tsx` call shape so the ported `UploadInsuranceSide`
  // can stay as a near-clone of the legacy component.
  const uploadInsuranceCard = (
    requestId: string,
    validationCode: string | number,
    file: File,
    side: 'front' | 'back',
    prMap: { [key: string]: string },
    surveyType?: string
  ): Promise<string> =>
    uploadFile({
      type: 'insuranceCard',
      requestId,
      validationCode,
      file,
      side,
      prMap,
      surveyType,
    });

  const skipUpload = async ({
    type,
    requestId,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
    // Legacy parameter — kept on call signatures while existing
    // callers transition. The schedule backend authenticates from
    // the bearer token, so we no longer send it on the wire.
    validationCode?: string | number;
  }): Promise<void> => {
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';

    await ChiroUpAPI.get(API, `/requests/${requestId}/${subPath}`, {
      queryParams: { skip: 'true' },
      headers: requestAuthHeaders(),
    });
  };

  // Insurance + basic information forms — kept for parity with the
  // legacy stepper. The bootstrap-level `requestId` is the umbrella
  // the user authed against; per-step request rows live under
  // `prMap` (one row per task type), and `/requests/{id}/insurance`
  // expects the insurance-specific row, not the umbrella. Falls back
  // to `requestId` for the portal `RequestFormRunner` path, where
  // `detail.id` is already the per-step row id.
  const insuranceCreate = async (
    requestId: string,
    _validationCode: string | number,
    insurances: Partial<HealthcomInsurance>[],
    kiosk?: boolean,
    prMap?: { [key: string]: string }
  ): Promise<Response> => {
    const pr = prMap?.insurance || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/insurance`, {
      body: insurances,
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  const skipInsuranceCreate = async (
    requestId: string,
    _validationCode?: string | number,
    kiosk?: boolean,
    prMap?: { [key: string]: string }
  ) => {
    const pr = prMap?.insurance || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/insurance`, {
      queryParams: { skip: 'true' },
      headers: requestAuthHeaders({ kiosk }),
      body: {},
    });
  };

  const submitInformation = async (
    requestId: string,
    data: Partial<Patient>,
    _validationCode: string | number,
    prMap?: { [key: string]: string },
    kiosk?: boolean
  ): Promise<Response> => {
    const pr = prMap?.basicInformation || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/information`, {
      body: data,
      headers: requestAuthHeaders({ kiosk }),
    });
  };

  // Whole-survey submit — used by the CCS / reevaluation flow which
  // posts every question + body-picker answer in one shot rather
  // than per-question. Routes to the legacy healthcom-backend (which
  // still owns the heavy CCS-specific orchestration: PatientTracks,
  // complaints, visit creation). Auth is the legacy validationCode
  // in the body, not the new bearer session.
  const submitSurvey = async (
    requestId: string,
    patientSurveyId: string,
    questions: SurveyQuestion[],
    kiosk: boolean,
    prMap?: { [key: string]: string },
    spanishQuestions?: SurveyQuestion[],
    ccsFollowups?: string[],
    validationCode?: string | number,
    encounterId?: string
  ): Promise<any> => {
    const pr = prMap?.[patientSurveyId] || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/survey/${patientSurveyId}`, {
      // On kiosk, force the kiosk token. Otherwise a stale patient/portal
      // session on the device wins the global fallback chain (main.tsx) and the
      // submit fails the ownership check (ticket 12256541103). Non-kiosk flows
      // (portal + request-link) keep using that chain, which picks correctly.
      ...(kiosk ? { headers: requestAuthHeaders({ kiosk: true }) } : {}),
      body: {
        questions,
        spanishQuestions,
        ccsFollowups,
        validationCode,
        prMap,
        kiosk,
        encounterId,
      },
    });
  };

  const submitForm = async ({
    requestId,
    patientFormId,
    fieldValues,
    signatures,
    validationCode,
    prMap,
    remainingSteps,
    kiosk,
  }: {
    requestId: string;
    patientFormId: string;
    fieldValues: Record<string, string>;
    signatures: Record<string, string>;
    validationCode: string | number;
    prMap?: { [key: string]: string };
    remainingSteps?: number[];
    kiosk?: boolean;
  }): Promise<{ documentId?: string }> => {
    const pr = prMap?.[patientFormId] || requestId;

    return ChiroUpAPI.put(API, `/requests/${pr}/form/${patientFormId}`, {
      headers: requestAuthHeaders({ kiosk }),
      body: {
        fieldValues,
        signatures,
        validationCode,
        remainingSteps: remainingSteps ?? [],
      },
    });
  };

  return {
    authRequest,
    submitSurveyAnswer,
    saveSurveyDraft,
    submitSurvey,
    getPreviousQuestion,
    submitConsent,
    uploadFile,
    uploadInsuranceCard,
    skipUpload,
    insuranceCreate,
    skipInsuranceCreate,
    submitInformation,
    submitForm,
  };
};

export default requestService();
